import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  LayoutDashboard,
  ClipboardList,
  TrendingUp,
  Trophy,
  BookOpen,
  Users,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  X,
  Wrench,
  ShieldCheck,
  Database,
  SlidersHorizontal,
  MessageSquareHeart,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/context/AuthContext';
import { useSidebar } from '@/context/SidebarContext';
import { useFeedback } from '@/context/FeedbackContext';
import Logo from '@/components/shared/Logo';

type NavItem = {
  key: string;
  label: string;
  icon: LucideIcon;
  to: string;
};

type NavGroup = {
  key: string;
  items: NavItem[];
};

function useNavGroups(): NavGroup[] {
  const { t } = useTranslation();
  const { isSalesManager, isSuperUser } = useAuth();

  // MVP: usuarios estándar (Salesperson/Trial) ven sólo el flujo nuevo —
  // home a dos botones, los dos flujos y sus resultados. Dashboards/ranking/
  // knowledge quedan ocultos (el código permanece, ver todo_new.md §8).
  const main: NavItem[] = isSuperUser
    ? [
        // SU sólo ve Inicio en el grupo principal: las vistas de salesperson/manager
        // se ocultan hasta tener vistas dedicadas para SU. El grupo SU (abajo) trae
        // Admin · Usuarios, Catálogo editorial, Scoring y Debug · Sesiones.
        { key: 'home', label: t('nav.home', 'Inicio'), icon: LayoutDashboard, to: '/' },
      ]
    : isSalesManager
      ? [
          { key: 'admin-home', label: t('nav.admin.home', 'Inicio'), icon: LayoutDashboard, to: '/admin' },
          { key: 'admin-simulations', label: t('nav.admin.simulations', 'Simulaciones'), icon: ClipboardList, to: '/admin/simulations' },
          { key: 'admin-progress', label: t('nav.progress', 'Progreso'), icon: TrendingUp, to: '/progress' },
          { key: 'admin-teams', label: t('nav.admin.teams', 'Equipos'), icon: Users, to: '/team' },
          { key: 'admin-ranking', label: t('nav.ranking', 'Ranking'), icon: Trophy, to: '/admin/ranking' },
          { key: 'knowledge', label: t('nav.knowledge', 'Conocimiento'), icon: BookOpen, to: '/knowledge' },
        ]
      : [
          { key: 'home', label: t('nav.home', 'Inicio'), icon: LayoutDashboard, to: '/' },
          { key: 'evaluate', label: t('nav.mvpEvaluate', 'Evalúa tu venta'), icon: ClipboardList, to: '/evaluate' },
          { key: 'pitch', label: t('nav.mvpPitch', 'Tu mejor pitch'), icon: Trophy, to: '/pitch' },
          { key: 'progress', label: t('nav.myResults', 'Mis resultados'), icon: TrendingUp, to: '/progress' },
        ];

  const groups: NavGroup[] = [{ key: 'main', items: main }];

  if (isSuperUser) {
    groups.push({
      key: 'superuser',
      items: [
        {
          key: 'superuser-admin-users',
          label: t('superuser.admin.nav.users', { defaultValue: 'Admin · Usuarios' }),
          icon: ShieldCheck,
          to: '/superuser/admin/users',
        },
        {
          key: 'superuser-catalog',
          label: t('superuser.catalog.nav', { defaultValue: 'Catálogo editorial' }),
          icon: Database,
          to: '/superuser/catalog',
        },
        {
          key: 'superuser-scoring',
          label: t('superuser.scoring.nav', { defaultValue: 'Scoring' }),
          icon: SlidersHorizontal,
          to: '/superuser/scoring',
        },
        {
          key: 'superuser-feedback',
          label: t('feedbackSurvey.admin.nav', { defaultValue: 'Feedback MVP' }),
          icon: MessageSquareHeart,
          to: '/superuser/feedback',
        },
        {
          key: 'superuser-debug-sessions',
          label: t('superuser.debug.nav.sessions', 'Debug · Sesiones'),
          icon: Wrench,
          to: '/superuser/debug/sessions',
        },
      ],
    });
  }

  return groups;
}

function useIsActive() {
  const location = useLocation();
  return (item: NavItem): boolean => {
    return location.pathname === item.to;
  };
}

function NavLink({ item, collapsed, active, onNavigate }: { item: NavItem; collapsed: boolean; active: boolean; onNavigate: () => void }) {
  const Icon = item.icon;
  const className = cn(
    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors outline-none',
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-sidebar',
    active
      ? 'bg-sidebar-primary text-sidebar-primary-foreground'
      : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
    collapsed && 'justify-center px-2'
  );

  const content = (
    <>
      <Icon className="size-[18px] shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link to={item.to} onClick={onNavigate} className={className} aria-label={item.label}>
            {content}
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Link to={item.to} onClick={onNavigate} className={className}>
      {content}
    </Link>
  );
}

export default function AppSidebar() {
  const { t } = useTranslation();
  const { collapsed, toggleCollapsed, isOpen, close } = useSidebar();
  const { open: openFeedback } = useFeedback();
  const groups = useNavGroups();
  const isActive = useIsActive();

  const widthClass = collapsed ? 'w-[72px]' : 'w-[256px]';

  return (
    <TooltipProvider delayDuration={200}>
      {/* Desktop sidebar (fixed) */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-40 hidden h-screen flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-300 lg:flex',
          widthClass
        )}
      >
        <SidebarInner
          t={t}
          collapsed={collapsed}
          groups={groups}
          isActive={isActive}
          onNavigate={() => {}}
          onToggleCollapse={toggleCollapsed}
          onFeedback={openFeedback}
        />
      </aside>

      {/* Mobile drawer */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={close}
          aria-hidden
        />
      )}
      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-screen w-[260px] flex flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-transform duration-300 lg:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        aria-hidden={!isOpen}
      >
        <SidebarInner
          t={t}
          collapsed={false}
          groups={groups}
          isActive={isActive}
          onNavigate={close}
          onToggleCollapse={null}
          onFeedback={openFeedback}
          mobileClose={close}
        />
      </aside>
    </TooltipProvider>
  );
}

type SidebarInnerProps = {
  t: TFunction;
  collapsed: boolean;
  groups: NavGroup[];
  isActive: (item: NavItem) => boolean;
  onNavigate: () => void;
  onToggleCollapse: (() => void) | null;
  onFeedback: () => void;
  mobileClose?: () => void;
};

function SidebarInner({ t, collapsed, groups, isActive, onNavigate, onToggleCollapse, onFeedback, mobileClose }: SidebarInnerProps) {
  return (
    <>
      {/* Logo row */}
      <div className={cn('flex h-16 items-center border-b border-sidebar-border px-4', collapsed && 'justify-center px-2')}>
        {collapsed ? (
          <Link to="/" aria-label="Home" className="rounded-md">
            <div className="flex size-9 items-center justify-center rounded-[10px] bg-gradient-to-br from-indigo-400 to-violet-400 text-lg">
              🎭
            </div>
          </Link>
        ) : (
          <Logo size="md" linkTo="/" />
        )}
        {mobileClose && (
          <Button variant="ghost" size="icon" className="ml-auto" onClick={mobileClose} aria-label={t('common.close', 'Cerrar')}>
            <X className="size-5" />
          </Button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {groups.map((group, idx) => (
          <div key={group.key}>
            {idx > 0 && (
              <div className="my-3 px-1">
                <Separator className="bg-sidebar-border" />
              </div>
            )}
            <ul className="space-y-1">
              {group.items.map((item) => (
                <li key={item.key}>
                  <NavLink item={item} collapsed={collapsed} active={isActive(item)} onNavigate={onNavigate} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <Separator className="bg-sidebar-border" />

      {/* Footer actions */}
      <div className="p-2 space-y-1">
        <Button
          variant="ghost"
          onClick={onFeedback}
          className={cn('h-10 w-full justify-start gap-3 rounded-lg text-sm font-medium text-sidebar-foreground/80 hover:text-sidebar-accent-foreground hover:bg-sidebar-accent', collapsed && 'justify-center px-2')}
          aria-label={t('nav.feedback', 'Feedback')}
        >
          <MessageSquare className="size-[18px]" />
          {!collapsed && <span>{t('nav.feedback', 'Feedback')}</span>}
        </Button>

        {onToggleCollapse && (
          <Button
            variant="ghost"
            onClick={onToggleCollapse}
            className={cn('h-10 w-full justify-start gap-3 rounded-lg text-sm font-medium text-sidebar-foreground/80 hover:text-sidebar-accent-foreground hover:bg-sidebar-accent', collapsed && 'justify-center px-2')}
            aria-label={collapsed ? t('nav.expand', 'Expandir') : t('nav.collapse', 'Colapsar')}
          >
            {collapsed ? <ChevronRight className="size-[18px]" /> : <ChevronLeft className="size-[18px]" />}
            {!collapsed && <span>{t('nav.collapse', 'Colapsar')}</span>}
          </Button>
        )}
      </div>
    </>
  );
}
